import React from 'react';
import { connect } from 'react-redux';
import { map } from 'lodash';
import { Card, NativeTable, Paragraph, Text } from 'elmo-elements';
import {
  AccountTreeSelector,
  LanguagePreferences,
  UserProfileFields,
  UserRole,
} from 'type';
import { getRoleName } from 'lib/helpers';
import { StoreState } from 'state/types';
import { getAccountTree } from 'state/AccountTree';
import { getLangPreferences } from 'state/Account';
import { hasPermissionSelector } from 'state/Auth';
import {
  BOX_USER_PROFILE_ADD_ROLE_REQUEST,
  BOX_USER_PROFILE_ROLES_MODAL_CLOSE,
  BOX_USER_PROFILE_ROLES_MODAL_OPEN,
  getErrors,
  getIsOpened,
  getIsUpdating,
  getUserProfile,
  getUserRoles,
} from 'state/Users/UserProfile';
import {
  RoleModal,
  RoleModalSubmitPayload,
} from '../../../../../../components';
import { EditButton } from '../';
import { FormattedErrors } from 'type';
import { NewPayRatesAlert } from './components/NewPayRatesAlert';
import './style.scss';

type StateProps = {
  roles: UserRole[];
  tree: AccountTreeSelector;
  isOpened: boolean;
  isUpdating: boolean;
  errors: FormattedErrors;
  langPreferences: LanguagePreferences;
  userProfile: UserProfileFields;
  hasPayRateEditPermission: boolean;
};

type DispatchProps = {
  closeModal: () => void;
  openModal: () => void;
  onFormSubmit: (data: RoleModalSubmitPayload) => void;
};

type Props = StateProps & DispatchProps;

export const RoleComponent = ({
  openModal,
  isOpened,
  isUpdating,
  closeModal,
  onFormSubmit,
  errors,
  roles,
  tree,
  userProfile,
  hasPayRateEditPermission,
}: Props) => {
  
  const getPayRate = (userRole: UserRole): string => {
    // Parse rate value - handle both number and string formats
    const rateValue = typeof userRole.pay_rate === 'number' 
      ? userRole.pay_rate 
      : parseFloat(String(userRole.pay_rate || 0));
    
    // Treat null, undefined, 0, or NaN as unassigned
    if (userRole.pay_rate === null || userRole.pay_rate === undefined || rateValue === 0 || isNaN(rateValue)) {
      return 'Unassigned';
    }

    return `£${rateValue.toFixed(2)} p/h`;
  };

  return (
    <Card
      isFullHeight={true}
      heading="Role"
      option={userProfile.is_active && <EditButton onClick={openModal} ariaLabel="Edit" />}
      className="role-card"
    >
      {userProfile.shouldShowNewPayRatesAlert && <NewPayRatesAlert />}

      <NativeTable isFullWidth={true} className="role-table">
        <NativeTable.Header>
          <NativeTable.Tr>
            {/* Combined column for mobile - hidden on desktop */}
            <NativeTable.Th className="role-table__col-combined">
              <Text size={'sm'}>Role</Text>
            </NativeTable.Th>
            {/* Separate columns for desktop - hidden on mobile */}
            <NativeTable.Th className="role-table__col-role" width={hasPayRateEditPermission ? '40%' : '50%'}>
              <Text size={'sm'}>Role</Text>
            </NativeTable.Th>
            <NativeTable.Th className="role-table__col-department" width={hasPayRateEditPermission ? '20%' : '25%'}>
              <Text size={'sm'}>Department</Text>
            </NativeTable.Th>
            <NativeTable.Th className="role-table__col-location" width={hasPayRateEditPermission ? '20%' : '25%'}>
              <Text size={'sm'}>Location</Text>
            </NativeTable.Th>
            {hasPayRateEditPermission && (
              <NativeTable.Th className="role-table__col-payrate" width={'20%'}>
                <Text size={'sm'}>Rate</Text>
              </NativeTable.Th>
            )}
          </NativeTable.Tr>
        </NativeTable.Header>
        <NativeTable.Body>
          {map(roles, (userRole: UserRole, index) => {
            const roleName = getRoleName(userRole, tree.roles, 'role');
            const areaName = getRoleName(userRole, tree.areas, 'area');
            const siteName = getRoleName(userRole, tree.sites, 'site');
            const payRate = getPayRate(userRole);
            const isUnassigned = payRate === 'Unassigned';

            return (
              <NativeTable.Tr
                key={userRole.role_id + userRole.area_id + userRole.site_id}
              >
                {/* Combined cell for mobile - stacked layout */}
                <NativeTable.Td className="role-table__col-combined">
                  <div className="role-combined-cell">
                    <div className="role-combined-cell__name">
                      <span className={`role-value role-value-${index}`}>{roleName}</span>
                      {userRole.is_main && (
                        <span className="role-badge role-badge-primary">Primary</span>
                      )}
                    </div>
                    <div className="role-combined-cell__details">
                      <span className="role-combined-cell__label">Dept:</span>
                      <span className={`department-value department-value-${index}`}>{areaName || '-'}</span>
                    </div>
                    <div className="role-combined-cell__details">
                      <span className="role-combined-cell__label">Location:</span>
                      <span className={`location-value location-value-${index}`}>{siteName || '-'}</span>
                    </div>
                    {hasPayRateEditPermission && (
                      <div className={`role-combined-cell__payrate ${isUnassigned ? 'role-combined-cell__payrate--error' : ''}`}>
                        <span className="role-combined-cell__label">Pay Rate:</span>
                        <span className={`pay-rate-value pay-rate-value-${index}`}>
                          {isUnassigned && <span className="pay-rate-warning-icon">⚠</span>}
                          {payRate}
                        </span>
                      </div>
                    )}
                  </div>
                </NativeTable.Td>
                {/* Separate cells for desktop */}
                <NativeTable.Td className="role-table__col-role">
                  <Paragraph className={`role-value role-value-${index}`}>
                    {roleName}
                    {userRole.is_main && (
                      <span className="role-badge role-badge-primary">Primary</span>
                    )}
                  </Paragraph>
                </NativeTable.Td>
                <NativeTable.Td className="role-table__col-department">
                  <Paragraph className={`department-value department-value-${index}`}>
                    {areaName || '-'}
                  </Paragraph>
                </NativeTable.Td>
                <NativeTable.Td className="role-table__col-location">
                  <Paragraph className={`location-value location-value-${index}`}>
                    {siteName || '-'}
                  </Paragraph>
                </NativeTable.Td>
                {hasPayRateEditPermission && (
                  <NativeTable.Td className="role-table__col-payrate">
                    <Paragraph className={`pay-rate-value pay-rate-value-${index}`}>
                      {isUnassigned && (
                        <span className="pay-rate-warning-icon">⚠</span>
                      )}
                      <span
                        className={isUnassigned ? 'pay-rate-text-error' : ''}
                      >
                        {payRate}
                      </span>
                    </Paragraph>
                  </NativeTable.Td>
                )}
              </NativeTable.Tr>
            );
          })}
        </NativeTable.Body>
      </NativeTable>

      <RoleModal
        isOpened={isOpened}
        isUpdating={isUpdating}
        onCloseModal={closeModal}
        onFormSubmit={onFormSubmit}
        errors={errors}
        initRoles={roles}
      />
    </Card>
  );
};

const mapStateToProps = (state: StoreState): StateProps => {
  const permission = hasPermissionSelector(state, 'payrate.edit');
  
  return {
    roles: getUserRoles(state),
    tree: getAccountTree(state),
    isOpened: getIsOpened(state, 'rolesModal'),
    isUpdating: getIsUpdating(state),
    errors: getErrors(state),
    langPreferences: getLangPreferences(state),
    userProfile: getUserProfile(state),
    hasPayRateEditPermission: permission,
  };
};

const mapDispatchToProps: DispatchProps = {
  closeModal: BOX_USER_PROFILE_ROLES_MODAL_CLOSE,
  openModal: BOX_USER_PROFILE_ROLES_MODAL_OPEN,
  onFormSubmit: BOX_USER_PROFILE_ADD_ROLE_REQUEST,
};

export const Role = connect(
  mapStateToProps,
  mapDispatchToProps
)(RoleComponent);